import axios from 'axios';
import { isReadOnlyMode, isSafeMethod, recordApiError } from './devTools';

// Base API instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

// Function to manage auth token
export const setAuthToken = (tokens) => {
  try {
    if (tokens && (tokens.access_token || tokens.access)) {
      const token = tokens.access_token || tokens.access;
      localStorage.setItem('authToken', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else if (tokens === null || tokens === undefined) {
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common['Authorization'];
    } else {
      console.warn('Invalid token format:', tokens);
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common['Authorization'];
    }
  } catch (error) {
    console.error('Error setting auth token:', error);
    // Ensure authorization header is removed in case of any error
    localStorage.removeItem('authToken');
    delete api.defaults.headers.common['Authorization'];
  }
};

/**
 * Caregiver auth token (FECOA-1514). Caregiver sessions live in sessionStorage,
 * not localStorage, so closing the tab/window clears the session (AC4) while a
 * page refresh keeps it. Staff/participant tokens stay in localStorage. A given
 * tab is only ever one role, so the request flow prefers the sessionStorage
 * (caregiver) token when present and otherwise falls back to localStorage.
 */
export const setCaregiverAuthToken = (token) => {
  if (token) {
    sessionStorage.setItem('authToken', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    sessionStorage.removeItem('authToken');
    const staffToken = localStorage.getItem('authToken');
    if (staffToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${staffToken}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }
};

// Initialize auth header from storage if a token exists (caregiver session token
// in sessionStorage takes precedence over a staff/participant localStorage token).
const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    // QA read-only mode (dev banner): reject any write so a tester can safely
    // "view as" another persona without mutating data. Token refresh uses a bare
    // axios.post (not this instance), so it is unaffected.
    // Auth endpoints are always exempt: a lingering read-only flag must never be
    // able to block login/logout/refresh, or a tester could get stranded on the
    // login page unable to authenticate.
    const isAuthEndpoint = /\/api\/(login|participant\/login|logout|token\/refresh)\/?$/.test(config.url || '');
    if (isReadOnlyMode() && !isSafeMethod(config.method) && !isAuthEndpoint) {
      const blocked = new Error('Blocked by QA read-only mode');
      blocked.isReadOnlyBlock = true;
      blocked.config = config;
      return Promise.reject(blocked);
    }

    // Always try to get the latest token (caregiver session token first).
    const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Surface the most recent failure to the dev banner (skip our own
    // read-only blocks — those aren't real backend errors).
    if (!error.isReadOnlyBlock) {
      recordApiError(error);
    }

    // Don't attempt token refresh for login endpoint
    if (error.response?.status === 401 && 
        (originalRequest.url?.includes('login') || 
         originalRequest.url?.includes('authstatus'))) {
      return Promise.reject(error);
    }

    // Shadow (QA view-as) tokens are short-lived and non-refreshable. If one
    // expired, don't refresh with the real user's refresh token (that would
    // silently re-auth as the real user while persona flags persist). Instead
    // end the shadow cleanly: restore the real token and drop persona state.
    if (error.response?.status === 401 && localStorage.getItem('authToken_real')) {
      const realToken = localStorage.getItem('authToken_real');
      localStorage.setItem('authToken', realToken);
      localStorage.removeItem('authToken_real');
      localStorage.removeItem('qa_persona');
      localStorage.removeItem('qa_readonly');
      console.warn('[API] Shadow session expired — restored real session');
      window.location.reload();
      return Promise.reject(error);
    }

    // Only attempt refresh if it's a 401 error, we haven't retried yet, and it's not a refresh token request
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('token/refresh')
    ) {
      originalRequest._retry = true;
      
      try {
        // Get refresh token from storage
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        // Make refresh token request
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/api/token/refresh/`,
          { refresh: refreshToken },
          { skipAuthRefresh: true } // Skip refresh for this request to avoid infinite loop
        );
        
        if (!response.data.tokens?.access_token) {
          throw new Error('Invalid token refresh response format');
        }
        
        const { tokens, user } = response.data;
        
        // Update tokens
        localStorage.setItem('authToken', tokens.access_token);
        if (tokens.refresh_token) {
          if (localStorage.getItem('refreshToken')) {
            localStorage.setItem('refreshToken', tokens.refresh_token);
          } else if (sessionStorage.getItem('refreshToken')) {
            sessionStorage.setItem('refreshToken', tokens.refresh_token);
          }
        }
        
        // Update user data if provided
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          // Update sponsor if changed
          if (user.sponsor_id) {
            const currentSponsor = JSON.parse(localStorage.getItem('selectedSponsor') || '{}');
            if (currentSponsor.id !== user.sponsor_id) {
              localStorage.setItem('selectedSponsor', JSON.stringify({
                id: user.sponsor_id,
                name: currentSponsor.name || 'Unknown Sponsor'
              }));
            }
          }
        }
        
        api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access_token}`;
        
        // Update the failed request's Authorization header
        originalRequest.headers['Authorization'] = `Bearer ${tokens.access_token}`;
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('refreshToken');

        // FECOA-1320/1514: caregiver sessions are a separate track (no refresh
        // token, their own login page) and live in sessionStorage. Route them
        // back to /caregiver-login, not the staff/participant /login, and clear
        // their sessionStorage token + marker.
        const isCaregiver = !!sessionStorage.getItem('caregiverSession');
        const loginPath = isCaregiver ? '/caregiver-login?expired=1' : '/login';
        if (isCaregiver) {
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('caregiverSession');
        }

        // Only do hard navigation if NOT already on the relevant login/flow page.
        const currentPath = window.location.pathname;
        const isOnLoginPage = isCaregiver
          ? currentPath.startsWith('/caregiver')
          : (currentPath === '/login' || currentPath.startsWith('/login'));

        if (!isOnLoginPage) {
          console.log('[API] Token refresh failed, redirecting to', loginPath, 'from:', currentPath);
          window.location.href = loginPath;
        } else {
          console.log('[API] Token refresh failed but already on login page, no redirect needed');
        }

        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Function to get a new instance with current auth state
export const getApi = () => api;

export default api;